import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Hidden } from '@mui/material';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import defaultLogo from 'assets/img/defaultLogo.png';
import defaultMiniLogo from 'assets/img/defaultMiniLogo.png';
import defaultFavIcon from 'assets/img/defaultFavIcon.ico';
import { Drawer, SidebarRef, StyledImage, StyledLogo, StyledLogoDefault, StyledLogoMini } from './SidebarStyle';
import { FavIcon } from '@totalsoft/rocket-ui';
import UserMenu from 'components/menu/user/UserMenu';
import Menu from 'components/menu/Menu';
import { sidebarWrapperHeight } from 'constants/sidebar';
import { getApplicationLogo } from 'utils/theme';

// We've created this component so we can have a ref to the wrapper of the links that appears in our sidebar.
// This was necessary so that we could initialize PerfectScrollbar on the links.
// There might be something with the Hidden component from material-ui, and we didn't have access to
// the links, and couldn't initialize the plugin.
function SidebarWrapper({ children, drawerOpen }) {
  const sidebarWrapperRef = useRef();

  return (
    <SidebarRef ref={sidebarWrapperRef} drawerOpen={drawerOpen}>
      <SimpleBar style={{ height: sidebarWrapperHeight, overflowX: 'hidden' }}>{children}</SimpleBar>
    </SidebarRef>
  );
}

SidebarWrapper.propTypes = {
  children: PropTypes.array.isRequired,
  drawerOpen: PropTypes.bool.isRequired
};

function Sidebar({ logoText, drawerOpen, drawerMinimized, changeLanguage, closeDrawer, withGradient }) {
  const { i18n } = useTranslation();
  const isSmallSidebar = !drawerMinimized; //drawerMinimized is not correctly set

  const [logoSrc, setLogoSrc] = useState(null);

  const onError = useCallback(() => {
    const defaultLogoSrc = isSmallSidebar ? defaultMiniLogo : defaultLogo;
    setLogoSrc(defaultLogoSrc);
  }, [isSmallSidebar]);

  useEffect(() => {
    const src = getApplicationLogo(isSmallSidebar);
    setLogoSrc(src);
  }, [isSmallSidebar]);

  const brand = (
    <StyledLogo>
      <StyledLogoMini href='/'>
        <StyledImage src={logoSrc} alt='logo' onError={onError} />
      </StyledLogoMini>
      {logoText && (
        <StyledLogoDefault href='/' drawerOpen={drawerMinimized}>
          {logoText}
        </StyledLogoDefault>
      )}
    </StyledLogo>
  );

  return (
    <div>
      <Hidden mdUp>
        <Drawer
          variant='temporary'
          anchor='right'
          open={drawerOpen}
          onClose={closeDrawer}
          ModalProps={{
            keepMounted: true // Better open performance on mobile.
          }}
          drawerOpen={drawerOpen}
        >
          {brand}
          <SidebarWrapper drawerOpen={drawerOpen}>
            <UserMenu drawerOpen={drawerOpen} changeLanguage={changeLanguage} language={i18n.language} withGradient={withGradient} />
            <Menu drawerOpen={drawerOpen} withGradient={withGradient} />
          </SidebarWrapper>
        </Drawer>
      </Hidden>
      <Hidden mdDown>
        <Drawer anchor='left' variant='permanent' drawerOpen={drawerMinimized}>
          {brand}
          <SidebarWrapper drawerOpen={drawerMinimized}>
            <UserMenu drawerOpen={drawerMinimized} changeLanguage={changeLanguage} language={i18n.language} withGradient={withGradient} />
            <Menu drawerOpen={drawerMinimized} withGradient={withGradient} />
          </SidebarWrapper>
        </Drawer>
      </Hidden>
      <FavIcon favIconSource={getApplicationLogo(true)} defaultFavIcon={defaultFavIcon} />
    </div>
  );
}

Sidebar.propTypes = {
  drawerOpen: PropTypes.bool.isRequired,
  drawerMinimized: PropTypes.bool.isRequired,
  closeDrawer: PropTypes.func.isRequired,
  changeLanguage: PropTypes.func.isRequired,
  logoText: PropTypes.string,
  withGradient: PropTypes.bool.isRequired
};

export default Sidebar;
